import { MelodyLib } from '@tunasong/music-lib'
import type { CaptureDiagnosis } from '@tunasong/music-lib'
import { melodyGrids } from '@tunasong/schemas'
import type { Melody, MelodyGrid, MelodyMeter, MelodySource } from '@tunasong/schemas'
import { Button, Select, TextField } from '@tunasong/ui-lib'
import { useState } from 'react'
import type { ChangeEvent, FC } from 'react'
import { useVocalCopy } from '../pitch-training/use-vocal-copy.js'
import { CaptureReview } from './capture-review.js'
import { rawNotesFromDownbeat, type FreeTempoFit } from './free-tempo-fit.js'
import styles from './free-tempo-review.module.css'

/**
 * §11.2 steps 4 and 5: what the fit found, what it means, and the same review §5 already ships.
 *
 * Two things are load-bearing here.
 *
 *  1. **The raw take is re-based on the FITTED downbeat and re-quantized, never re-recorded.** The
 *     tempo, meter and grid are editable and every edit re-quantizes the same raw notes, which is
 *     what §11.3 means by retaining the take: choosing the grid is a decision the user may take
 *     several times, and none of them costs another performance.
 *  2. **A bad fit is not an error state.** The copy says the beat was not found, the notes stay
 *     exactly where they were sung, the offsets carry the difference, and Accept is as available as
 *     it is for a clean take (§11.4).
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §11.2, §11.4
 */

const GRID_LABEL: Record<MelodyGrid, string> = {
  '8': '1/8',
  '16': '1/16',
  '8t': '1/8 triplet',
  '16t': '1/16 triplet',
}

const BEATS_PER_BAR = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]

export interface FreeTempoReviewProps {
  melody: Melody
  /** The retained raw take, on the TAKE's clock - not yet re-based on a downbeat. */
  raw: MelodyLib.RawNote[]
  fit: FreeTempoFit
  source: MelodySource
  /** Set for an audio take, so an empty one can say which of the three things went wrong. */
  diagnosis?: CaptureDiagnosis | null
  onAccept: (melody: Melody) => void
  /** Back to the taps. NOT back to the microphone - that is `onRetake`. */
  onRetap: () => void
  onRetake: () => void
  onCancel: () => void
  className?: string
}

export const FreeTempoReview: FC<FreeTempoReviewProps> = props => {
  const { melody, raw, fit, source, diagnosis = null, onAccept, onRetap, onRetake, onCancel, className } = props

  /**
   * The fitted tempo is rounded for the field and left unrounded nowhere else - `melodySchema`
   * accepts any number in 20-300, but a field reading 96.0522 is not a number anyone types over.
   */
  const [bpm, setBpm] = useState(Math.round(fit.bpm))
  const [meter, setMeter] = useState<MelodyMeter>(melody.tempo.meter)
  const [grid, setGrid] = useState<MelodyGrid>(fit.grid)

  const quality = useVocalCopy(fit.verdict, { ms: Math.round(fit.rmsDeviationMs) })

  /** Out-of-range values are DROPPED, not clamped - the rule `CaptureSettings` states. */
  const handleBpm = (event: ChangeEvent<HTMLInputElement>) => {
    const next = Number(event.target.value)
    if (!Number.isFinite(next) || next < 20 || next > 300) {
      return
    }
    setBpm(next)
  }

  const handleMeterTop = (event: ChangeEvent<HTMLSelectElement>) =>
    setMeter(current => ({ ...current, top: Number(event.target.value) }))

  const handleMeterBottom = (event: ChangeEvent<HTMLSelectElement>) =>
    setMeter(current => ({ ...current, bottom: event.target.value === '8' ? 8 : 4 }))

  const handleGrid = (event: ChangeEvent<HTMLSelectElement>) => setGrid(event.target.value as MelodyGrid)

  const fitted: Melody = { ...melody, tempo: { bpm, meter }, grid }

  /**
   * Where beat 1 actually goes, which is not always where the user tapped.
   *
   * A pickup precedes the first full bar, so the origin retreats in whole bars to make room for it
   * (§11 review). Recomputed from the CURRENT tempo, meter and grid rather than taken from the fit,
   * because all three are editable here and a bar is only as long as the meter says it is.
   */
  const origin = MelodyLib.originForPickup(
    fit.phaseMs,
    raw.map(note => note.onsetMs),
    { bpm, meter, grid }
  )

  return (
    <div className={className ? `${styles.root} ${className}` : styles.root}>
      <div className={styles.quality} role="status" aria-label="Fit quality">
        {quality ? <span>{quality.body}</span> : null}
        <span className={styles.qualityDetail}>
          Fitted from your taps: {Math.round(fit.bpm)} BPM, {Math.round(fit.rmsDeviationMs)} ms average distance from
          the beat.
        </span>
      </div>

      <div className={styles.controls}>
        <TextField
          className={styles.control}
          label="Tempo (BPM)"
          type="number"
          inputProps={{ min: 20, max: 300 }}
          value={bpm}
          onChange={handleBpm}
        />

        <Select className={styles.control} label="Beats per bar" value={meter.top} onChange={handleMeterTop}>
          {BEATS_PER_BAR.map(top => (
            <option key={top} value={top}>
              {top}
            </option>
          ))}
        </Select>

        <Select className={styles.control} label="Beat unit" value={meter.bottom} onChange={handleMeterBottom}>
          <option value={4}>4</option>
          <option value={8}>8</option>
        </Select>

        <Select className={styles.control} label="Grid" value={grid} onChange={handleGrid}>
          {melodyGrids.map(value => (
            <option key={value} value={value}>
              {GRID_LABEL[value]}
            </option>
          ))}
        </Select>

        <Button onClick={onRetap}>Tap the beat again</Button>
      </div>

      {/*
        `CaptureReview` holds its raw notes in state, so anything that moves the origin - a re-tap,
        or a meter change that resizes the bar a pickup lives in - has to remount it. Without the key
        it would silently re-quantize the take it was first given.
      */}
      <CaptureReview
        key={origin}
        melody={fitted}
        raw={rawNotesFromDownbeat(raw, origin)}
        source={source}
        diagnosis={diagnosis}
        onAccept={onAccept}
        onRetake={onRetake}
        onCancel={onCancel}
      />
    </div>
  )
}
